import React, { useState, useRef, useEffect } from "react";
import { useLiveview } from "../../hooks/useLiveview";
import { Box, IconButton, Slider, Stack, Tooltip, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { FormattedMessage } from "react-intl";
import VideocamIcon from "@mui/icons-material/Videocam";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import SettingsIcon from "@mui/icons-material/Settings";
import { useNavigate } from "react-router-dom";
import AspectRatioIcon from "@mui/icons-material/AspectRatio";
import CloseIcon from "@mui/icons-material/Close";
import { isCameraDragEvent, getCameraDragData, setCameraDragData } from "../../lib/cameraDrag";
import { CameraStatusIcon } from "../ResourceSidebar/DeviceItem";
import SnapshotModal from "../modal/SnapshotModal";
import ManualRecordingModal from "../modal/ManualRecordingModal";
import GlobalAlert from "components/Notification";

const { DEVICE_RTSP_MAX_CONNECTIONS_REACHED } = require("../../lib/rtsp");

/**
 * CameraCell
 *
 * A single slot in the video wall grid. Manages its own hover and toolbar state.
 *
 * Props:
 *   slotIndex  - grid slot index (written to data attribute for stream attachment)
 *   slot       - { deviceId, name, linkStatus?, accessToken, falconEndpoint, relayProtocol, streamId }
 *   onAssignCamera - (camera, slotIndex) when a sidebar camera is dropped here
 *   onMoveSlot     - (fromSlotIndex, toSlotIndex) when a grid camera is dragged to another slot
 *   onRemoveCamera - (slotIndex) when the user removes a camera from the slot
 *   extraSx    - additional sx overrides (e.g. gridArea, aspectRatio)
 */
const CameraCell = ({ slotIndex, slot, onAssignCamera, onMoveSlot, onRemoveCamera, onRequestStartViewing, layout, extraSx = {} }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [hovered, setHovered] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [streamIdOverride, setStreamIdOverride] = useState(null);
  const [snapshot, setSnapshot] = useState(null);
  const [recording, setRecording] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const [recordingBlob, setRecordingBlob] = useState(null);
  const [recordingMimeType, setRecordingMimeType] = useState("");
  const [alertInfo, setAlertInfo] = useState({ open: false, severity: "info", message: "" });
  const [digitalZoomOpen, setDigitalZoomOpen] = useState(false);
  const [digitalZoom, setDigitalZoom] = useState(100);
  const [aspectRatioIndex, setAspectRatioIndex] = useState(0);
  const [isCameraFullscreen, setIsCameraFullscreen] = useState(false);
  const [zoomPanelPosition, setZoomPanelPosition] = useState({ left: 8, top: null });
  const [isMuted, setIsMuted] = useState(layout === "1x1" ? false : true);

  useEffect(() => {
    setIsMuted(layout === "1x1" ? false : true);
  }, [slotIndex, slot?.deviceId, layout]);
  const videoRef = useRef(null);
  const cellRef = useRef(null);
  const zoomPanelRef = useRef(null);
  const zoomButtonRef = useRef(null);
  const zoomDragRef = useRef(null);
  const autoStartedRef = useRef("");
  const recorderRef = useRef(null);
  const recordingStreamRef = useRef(null);
  const recordingChunksRef = useRef([]);
  const recordingStartedAtRef = useRef(0);

  const showAlert = (severity, message) => {
    setAlertInfo({ open: true, severity, message });
  };

  const assigned = Boolean(slot?.deviceId);
  const offline = assigned && slot?.linkStatus !== "online";
  const falconEndpoint = assigned && !offline ? (slot?.falconEndpoint ?? "") : "";
  const relayProtocol = slot?.relayProtocol || "ws";
  const accessToken = slot?.accessToken ?? "";
  const streamId = streamIdOverride !== null ? streamIdOverride : (slot?.streamId ?? 1);

  // Reset streamIdOverride when slot's streamId changes (e.g., when switching views)
  useEffect(() => {
    setStreamIdOverride(null);
  }, [slot?.streamId, slot?.deviceId]);

  const { status, start, pause } = useLiveview({
    videoRef,
    falconEndpoint,
    relayProtocol,
    deviceId: slot?.deviceId ?? "",
    deviceSiteId: slot?.siteId ?? "",
    accessToken: assigned && !offline ? accessToken : "",
    streamId,
    requestStartViewingCommand: assigned && !offline ? onRequestStartViewing : undefined,
    enabled: assigned && !offline,
    muted: isMuted,
  });

  // Auto-start once when a camera is assigned to this cell
  useEffect(() => {
    const canStream =
      assigned &&
      !offline &&
      accessToken &&
      (onRequestStartViewing || falconEndpoint);
    const key = canStream
      ? `${slot.deviceId}:${accessToken}:${streamId}:${onRequestStartViewing ? "cmd" : falconEndpoint}`
      : "";
    if (!key) {
      autoStartedRef.current = "";
      return;
    }
    if (autoStartedRef.current === key) return;
    autoStartedRef.current = key;
    let cancelRetry = () => {};
    const timer = setTimeout(() => {
      cancelRetry = start() || (() => {});
    }, 100);
    return () => {
      clearTimeout(timer);
      cancelRetry();
    };
  }, [slot?.deviceId, accessToken, falconEndpoint, streamId, start, offline, assigned, onRequestStartViewing]);

  const connectionLimitReached = status.includes(DEVICE_RTSP_MAX_CONNECTIONS_REACHED);
  const playing = !offline && status !== "idle" && status !== "paused" && !status.startsWith("Error");
  const aspectRatioOptions = ["AUTO", "16:9", "4:3", "9:16", "3:4"];
  const selectedAspectRatio = aspectRatioOptions[aspectRatioIndex];
  const aspectRatioValue = selectedAspectRatio === "AUTO"
    ? null
    : selectedAspectRatio.replace(":", " /");
  const isPortraitAspectRatio = aspectRatioValue
    ? Number(aspectRatioValue.split(" /")[0]) < Number(aspectRatioValue.split(" /")[1])
    : false;

  const handleAspectRatioChange = (event) => {
    event.stopPropagation();
    setAspectRatioIndex((index) => (index + 1) % aspectRatioOptions.length);
  };

  useEffect(() => {
    if (!digitalZoomOpen) return undefined;

    const handleOutsidePointerDown = (event) => {
      if (!zoomPanelRef.current?.contains(event.target) && !zoomButtonRef.current?.contains(event.target)) {
        setDigitalZoomOpen(false);
      }
    };
    document.addEventListener("pointerdown", handleOutsidePointerDown);
    return () => document.removeEventListener("pointerdown", handleOutsidePointerDown);
  }, [digitalZoomOpen]);

  useEffect(() => {
    setDigitalZoom(100);
    setDigitalZoomOpen(false);
    setZoomPanelPosition({ left: 8, top: null });
    setAspectRatioIndex(0);
    setIsCameraFullscreen(false);
  }, [slot?.deviceId]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsCameraFullscreen(document.fullscreenElement === cellRef.current);
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  useEffect(() => () => {
    document.removeEventListener("pointermove", zoomDragRef.current?.onMove);
    document.removeEventListener("pointerup", zoomDragRef.current?.onUp);
  }, []);

  const handleZoomPanelDragStart = (event) => {
    if (event.button !== 0 || !cellRef.current || !zoomPanelRef.current) return;
    event.preventDefault();
    event.stopPropagation();

    const cellRect = cellRef.current.getBoundingClientRect();
    const panelRect = zoomPanelRef.current.getBoundingClientRect();
    const offsetX = event.clientX - panelRect.left;
    const offsetY = event.clientY - panelRect.top;
    const onMove = (moveEvent) => {
      const maxLeft = Math.max(0, cellRect.width - panelRect.width);
      const maxTop = Math.max(0, cellRect.height - panelRect.height);
      setZoomPanelPosition({
        left: Math.min(maxLeft, Math.max(0, moveEvent.clientX - cellRect.left - offsetX)),
        top: Math.min(maxTop, Math.max(0, moveEvent.clientY - cellRect.top - offsetY)),
      });
    };
    const onUp = () => {
      document.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerup", onUp);
      zoomDragRef.current = null;
    };
    zoomDragRef.current = { onMove, onUp };
    document.addEventListener("pointermove", onMove);
    document.addEventListener("pointerup", onUp);
  };

  const handleFullscreen = async (event) => {
    event.stopPropagation();
    if (!cellRef.current) return;

    try {
      if (document.fullscreenElement === cellRef.current) {
        await document.exitFullscreen();
      } else if (!document.fullscreenElement) {
        await cellRef.current.requestFullscreen();
      }
    } catch (error) {
      showAlert("error", "Fullscreen is not supported by this browser.");
    }
  };

  const handleDragEnter = (e) => {
    if (!isCameraDragEvent(e)) return;
    e.preventDefault();
  };

  const handleDragOver = (e) => {
    if (!isCameraDragEvent(e)) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "move";
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setDragOver(false);
  };

  const handleDragStart = (e) => {
    if (!assigned || e.target.closest("button")) {
      e.preventDefault();
      return;
    }
    setCameraDragData(
      e,
      { id: slot.deviceId, name: slot.name ?? slot.deviceId },
      { sourceSlotIndex: slotIndex }
    );
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    const camera = getCameraDragData(e);
    if (!camera?.id) return;

    if (camera.sourceSlotIndex != null) {
      onMoveSlot?.(camera.sourceSlotIndex, slotIndex);
      return;
    }

    onAssignCamera?.(camera, slotIndex);
  };

  const handleSnapshot = (e) => {
    e.stopPropagation();
    const video = videoRef.current;
    if (!video || video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA || !video.videoWidth || !video.videoHeight) return;

    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const context = canvas.getContext("2d");
    if (!context) return;

    try {
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      setSnapshot(canvas.toDataURL("image/jpeg", 1));
    } catch (error) {
      // A protected stream cannot be copied to canvas.
      setSnapshot(null);
    }
  };

  const handleSaveSnapshot = () => {
    if (!snapshot) return;
    const anchor = document.createElement("a");
    anchor.href = snapshot;
    const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
    anchor.download = `${(slot?.name || slot?.deviceId || "camera").replace(/[^a-z0-9_-]+/gi, "-")}-snapshot-${timestamp}.jpg`;
    anchor.click();
    setSnapshot(null);
  };

  const stopRecording = () => {
    if (recorderRef.current?.state === "recording") recorderRef.current.stop();
  };

  const handleRecording = (e) => {
    e.stopPropagation();
    if (recording) {
      stopRecording();
      return;
    }

    const video = videoRef.current;
    if (!video?.captureStream || typeof MediaRecorder === "undefined") {
      showAlert("error", "Manual recording is not supported by this browser.");
      return;
    }

    const stream = video.captureStream();
    const mimeType = [
      "video/mp4;codecs=avc1.42E01E,mp4a.40.2",
      "video/mp4",
      "video/webm;codecs=vp9,opus",
      "video/webm;codecs=vp8,opus",
      "video/webm",
    ]
      .find((type) => MediaRecorder.isTypeSupported(type));
    if (!mimeType) {
      showAlert("error", "Manual recording is not supported by this browser.");
      return;
    }

    const recorder = new MediaRecorder(stream, { mimeType });
    recordingChunksRef.current = [];
    recordingStartedAtRef.current = Date.now();
    recorder.ondataavailable = (event) => {
      if (event.data.size) recordingChunksRef.current.push(event.data);
    };
    recorder.onstop = () => {
      const blob = new Blob(recordingChunksRef.current, { type: mimeType });
      recordingChunksRef.current = [];
      recordingStreamRef.current?.getTracks().forEach((track) => track.stop());
      recordingStreamRef.current = null;
      recorderRef.current = null;
      setRecording(false);
      setRecordingSeconds(0);
      if (blob.size) {
        setRecordingMimeType(mimeType);
        setRecordingBlob(blob);
      }
    };
    recorderRef.current = recorder;
    recordingStreamRef.current = stream;
    recorder.start();
    setRecording(true);
    showAlert("info", (
      <Stack spacing={0.25}>
        <Typography sx={{ fontWeight: 700, fontSize: 14 }}>Reminder</Typography>
        <Typography sx={{ fontSize: 13 }}>Manual recording is limited to 10 minutes.</Typography>
      </Stack>
    ));
  };

  useEffect(() => {
    if (!recording) return undefined;
    const timer = setInterval(() => {
      const seconds = Math.floor((Date.now() - recordingStartedAtRef.current) / 1000);
      setRecordingSeconds(seconds);
      if (seconds >= 600) stopRecording();
    }, 1000);
    return () => clearInterval(timer);
  }, [recording]);

  useEffect(() => () => {
    if (recorderRef.current) {
      recorderRef.current.onstop = null;
      if (recorderRef.current.state !== "inactive") recorderRef.current.stop();
    }
    recordingStreamRef.current?.getTracks().forEach((track) => track.stop());
  }, [slot?.deviceId]);

  const discardRecording = () => {
    setRecordingBlob(null);
  };

  const saveRecording = async () => {
    if (!recordingBlob) return;
    const safeName = (slot?.name || slot?.deviceId || "camera").replace(/[^a-z0-9_-]+/gi, "-");
    const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
    const isMp4 = recordingMimeType.startsWith("video/mp4");
    const extension = isMp4 ? "mp4" : "webm";
    const fileName = `${safeName}-manual-recording-${timestamp}.${extension}`;

    try {
      if (window.showSaveFilePicker) {
        const fileHandle = await window.showSaveFilePicker({
          suggestedName: fileName,
          types: [{
            description: isMp4 ? "MP4 video" : "WebM video",
            accept: { [isMp4 ? "video/mp4" : "video/webm"]: [`.${extension}`] },
          }],
        });
        const writable = await fileHandle.createWritable();
        await writable.write(recordingBlob);
        await writable.close();
      } else {
        const url = URL.createObjectURL(recordingBlob);
        const anchor = document.createElement("a");
        anchor.href = url;
        anchor.download = fileName;
        anchor.click();
        URL.revokeObjectURL(url);
      }
      setRecordingBlob(null);
    } catch (error) {
      if (error.name !== "AbortError") {
        showAlert("error", "Unable to save the recording.");
      }
    }
  };

  const handlePlayback = (e) => {
    e.stopPropagation();
    if (!slot?.deviceId || offline) return;
    navigate(`/surveillance/playback?deviceId=${encodeURIComponent(slot.deviceId)}`);
  };

  const iconBtnSx = {
    color: "#fff",
    width: 20,
    height: 20,
    minWidth: 0,
    flexShrink: 0,
    p: 0,
    "& .MuiSvgIcon-root": {
      fontSize: 15,
    },
    "&:hover": { bgcolor: "rgba(255,255,255,0.15)" },
  };

  return (
    <Box
      data-slot-index={slotIndex}
      data-device-id={slot?.deviceId ?? ""}
      ref={cellRef}
      draggable={assigned}
      onDragStart={handleDragStart}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        border: dragOver
          ? `2px dashed ${theme.palette.primary.main}`
          : "1px solid rgba(0,0,0,0.1)",
        overflow: "hidden",
        cursor: assigned ? "grab" : "pointer",
        backgroundColor: assigned ? theme.palette.grey[900] : theme.palette.grey[100],
        transition: "background-color 0.15s, border-color 0.15s",
        "&:fullscreen": {
          width: "100vw",
          height: "100vh",
          border: "none",
          backgroundColor: "#000",
        },
        "&:hover": {
          backgroundColor: assigned ? theme.palette.grey[800] : theme.palette.grey[200],
        },
        ...extraSx,
      }}
    >
      {/* Camera name — top-left header */}
      {assigned && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            right: 8,
            left: "auto",
            zIndex: 2,
            p: "4px 10px",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "flex-end",
            background: "rgba(17, 17, 17, 0.22)",
            border: "1px solid rgba(255,255,255,0.12)",
            borderRadius: "6px",
            boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
            backdropFilter: "blur(1.5px)",
            maxWidth: "100%",
          }}
        >
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 700,
              color: "#fff",
              textShadow: "0 1px 2px rgba(0,0,0,0.7)",
              lineHeight: 1.3,
              whiteSpace: "nowrap",
              overflow: "visible",
              textOverflow: "clip",
              letterSpacing: "0.02em",
            }}
          >
            {slot.name ?? slot.deviceId}
          </Typography>
        </Box>
      )}

      {/* Debug status text intentionally hidden to keep the camera overlay clean */}

      {/* Video stream */}
      {assigned && !offline && (
        <video
          ref={videoRef}
          autoPlay
          muted={isMuted}
          playsInline
          style={{
            position: "absolute",
            inset: 0,
            width: aspectRatioValue && isPortraitAspectRatio ? "auto" : "100%",
            height: aspectRatioValue ? (isPortraitAspectRatio ? "100%" : "auto") : "100%",
            maxWidth: "100%",
            maxHeight: "100%",
            aspectRatio: aspectRatioValue || "auto",
            objectFit: aspectRatioValue ? "cover" : "contain",
            backgroundColor: "#000",
            zIndex: 1,
            pointerEvents: "none",
            margin: aspectRatioValue ? "auto" : undefined,
            transform: `scale(${digitalZoom / 100})`,
            transformOrigin: "center center",
            transition: "transform 0.1s ease-out",
          }}
        />
      )}

      {/* Offline placeholder */}
      {offline && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1,
            zIndex: 1,
          }}
        >
          <Box sx={{ transform: "scale(1.6)" }}>
            <CameraStatusIcon online={false} />
          </Box>
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 500,
              color: theme.palette.grey[400],
            }}
          >
            <FormattedMessage id="liveview.camera.offline" defaultMessage="Offline" />
          </Typography>
        </Box>
      )}

      {/* Relay: camera RTSP connection limit reached */}
      {assigned && !offline && connectionLimitReached && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            px: 1.5,
            zIndex: 2,
            backgroundColor: "rgba(0,0,0,0.55)",
            pointerEvents: "none",
          }}
        >
          <Typography
            role="status"
            sx={{
              fontSize: 13,
              fontWeight: 600,
              color: "#fff",
              textAlign: "center",
              lineHeight: 1.4,
              textShadow: "0 1px 2px rgba(0,0,0,0.6)",
            }}
          >
            <FormattedMessage
              id="liveview.camera.connection_limit_reached"
              defaultMessage="Device stream connection limit reached"
            />
          </Typography>
        </Box>
      )}

      {/* Center placeholder icon — only when empty */}
      {!assigned && (
        <VideocamIcon sx={{ fontSize: 36, color: theme.palette.grey[400] }} />
      )}

      {/* Hover action toolbar */}
      {hovered && assigned && (
        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: "1px",
            py: "1px",
            background: "linear-gradient(to top, rgba(0,0,0,0.52), rgba(0,0,0,0.2) 45%, transparent 100%)",
            zIndex: 3,
            overflowX: "auto",
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", flexWrap: "nowrap", gap: 0, minWidth: 0 }}>
            <IconButton
              size="small"
              disabled={offline}
              onClick={(e) => { e.stopPropagation(); if (!offline) (playing ? pause() : start()); }}
              sx={iconBtnSx}
            >
              {playing ? (
                <PauseIcon sx={{ fontSize: 16 }} />
              ) : (
                <PlayArrowIcon sx={{ fontSize: 16 }} />
              )}
            </IconButton>

            <IconButton
              size="small"
              disabled={offline}
              onClick={(e) => {
                e.stopPropagation();
                const nextStreamId = streamId === 1 ? 2 : 1;
                setStreamIdOverride(nextStreamId);
                // Pause current stream; streamId change will auto-trigger restart via useEffect
                if (playing) {
                  pause();
                }
              }}
              sx={iconBtnSx}
              title={streamId === 1 ? "Switch to SD" : "Switch to HD"}
            >
              <Box sx={{ fontSize: 11, fontWeight: "bold", color: "#fff" }}>
                {streamId === 1 ? "SD" : "HD"}
              </Box>
            </IconButton>

            <Tooltip title={isMuted ? "Unmute" : "Mute"}>
              <IconButton
                size="small"
                disabled={offline}
                onClick={(e) => {
                  e.stopPropagation();
                  setIsMuted((prev) => !prev);
                }}
                sx={iconBtnSx}
                aria-label={isMuted ? "Unmute" : "Mute"}
              >
                {isMuted ? (
                  <VolumeOffIcon sx={{ fontSize: 16, color: "rgba(255,255,255,0.6)" }} />
                ) : (
                  <VolumeUpIcon sx={{ fontSize: 16 }} />
                )}
              </IconButton>
            </Tooltip>

            <IconButton size="small" disabled={offline} onClick={handleSnapshot} sx={iconBtnSx} title="Take snapshot">
              <PhotoCameraIcon sx={{ fontSize: 16 }} />
            </IconButton>

            <IconButton size="small" disabled={offline || Boolean(recordingBlob)} onClick={handleRecording} sx={iconBtnSx} title={recording ? "Stop recording" : "Start recording"}>
              {recording ? <PauseIcon sx={{ fontSize: 16 }} /> : <RadioButtonCheckedIcon sx={{ fontSize: 16 }} />}
            </IconButton>

            <IconButton size="small" disabled={offline} onClick={handlePlayback} sx={iconBtnSx} title="Open playback">
              <PlayCircleOutlineIcon sx={{ fontSize: 16 }} />
            </IconButton>

            <Tooltip title="Digital Zoom">
              <IconButton
                ref={zoomButtonRef}
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setDigitalZoomOpen((open) => !open);
                }}
                sx={{ ...iconBtnSx, color: digitalZoomOpen ? "#ff9800" : iconBtnSx.color }}
                aria-label="Digital Zoom"
              >
                <ZoomInIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>

            <Tooltip title={`Aspect Ratio: ${selectedAspectRatio}`}>
              <IconButton
                size="small"
                onClick={handleAspectRatioChange}
                sx={iconBtnSx}
                aria-label={`Aspect Ratio ${selectedAspectRatio}`}
              >
                <AspectRatioIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>

            <Tooltip title={isCameraFullscreen ? "Exit Fullscreen" : "Fullscreen"}>
              <IconButton
                size="small"
                onClick={handleFullscreen}
                sx={iconBtnSx}
                aria-label={isCameraFullscreen ? "Exit Fullscreen" : "Fullscreen"}
              >
                {isCameraFullscreen ? (
                  <FullscreenExitIcon sx={{ fontSize: 16 }} />
                ) : (
                  <FullscreenIcon sx={{ fontSize: 16 }} />
                )}
              </IconButton>
            </Tooltip>

            <IconButton size="small" onClick={(e) => e.stopPropagation()} sx={iconBtnSx}>
              <SettingsIcon sx={{ fontSize: 16 }} />
            </IconButton>

            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onRemoveCamera?.(slotIndex);
              }}
              sx={{
                ...iconBtnSx,
                color: "rgba(255,255,255,0.85)",
                "&:hover": { bgcolor: "rgba(220,53,69,0.7)" },
              }}
              title="Remove camera"
            >
              <CloseIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Box>

        </Box>
      )}

      {digitalZoomOpen && assigned && (
        <Box
          ref={zoomPanelRef}
          draggable={false}
          onPointerDown={(e) => e.stopPropagation()}
          sx={{
            position: "absolute",
            left: zoomPanelPosition.left,
            ...(zoomPanelPosition.top === null ? { bottom: 42 } : { top: zoomPanelPosition.top }),
            zIndex: 5,
            width: "min(220px, calc(100% - 16px))",
            p: "10px 12px 8px",
            color: "#172664",
            backgroundColor: "rgba(255,255,255,0.98)",
            border: "1px solid rgba(23,38,100,0.16)",
            borderRadius: "4px",
            boxShadow: "0 3px 12px rgba(0,0,0,0.28)",
          }}
        >
          <Box
            draggable={false}
            onPointerDown={handleZoomPanelDragStart}
            sx={{
              cursor: "move",
              userSelect: "none",
              touchAction: "none",
            }}
          >
            <Typography sx={{ fontSize: 12, fontWeight: 700, mb: 0.5 }}>
              Digital Zoom
            </Typography>
            <IconButton
              size="small"
              onClick={() => setDigitalZoomOpen(false)}
              onPointerDown={(e) => e.stopPropagation()}
              aria-label="Close Digital Zoom"
              sx={{
                position: "absolute",
                top: 6,
                right: 8,
                p: 0.25,
                color: "#172664",
                "&:hover": { backgroundColor: "rgba(23,38,100,0.08)" },
              }}
            >
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Slider
              aria-label="Digital Zoom"
              value={digitalZoom}
              min={100}
              max={400}
              step={1}
              onChange={(_, value) => setDigitalZoom(value)}
              size="small"
              sx={{
                flex: 1,
                color: "#172664",
                height: 4,
                "& .MuiSlider-thumb": { width: 12, height: 12 },
              }}
            />
            <Typography sx={{ minWidth: 42, fontSize: 12, fontWeight: 700, textAlign: "right" }}>
              {digitalZoom}%
            </Typography>
          </Stack>
        </Box>
      )}

      <SnapshotModal
        open={Boolean(snapshot)}
        snapshot={snapshot}
        onClose={() => setSnapshot(null)}
        onSave={handleSaveSnapshot}
      />

      {recording && (
        <Typography
          sx={{
            position: "absolute",
            top: 24,
            right: 8,
            zIndex: 3,
            px: 0.75,
            py: 0.25,
            color: "#fff",
            backgroundColor: "rgba(180, 0, 0, 0.8)",
            borderRadius: 0.5,
            fontSize: 11,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {`${String(Math.floor(recordingSeconds / 60)).padStart(2, "0")}:${String(recordingSeconds % 60).padStart(2, "0")}`}
        </Typography>
      )}

      <ManualRecordingModal
        open={Boolean(recordingBlob)}
        onDiscard={discardRecording}
        onSave={saveRecording}
      />

      <GlobalAlert
        open={alertInfo.open}
        severity={alertInfo.severity}
        message={alertInfo.message}
        onClose={() => setAlertInfo((prev) => ({ ...prev, open: false }))}
      />
    </Box>
  );
};

export default CameraCell;
